// PanelAgentTemplate 中点击模板项打开视频弹窗
import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowLeft, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Image } from "@/components/common/image";
import { TruncatedText } from "@/components/common/truncated-text";
import { AgentTemplateVideoModal } from "@/components/agent-modal-template-video";
import { CloseIcon, MoreIcon, TrashIcon } from "@/components/ui/icon";
import type { TemplateItem } from "@/components/agent-panel-subscription-settings/templates-carousel";
import { TemplateEmptyIcon } from "../agent-dashboard-user-detail/assets";
import { cn } from "@/lib/utils";
import { ModalCloseButton } from "../common/modal-close-button";
import { MoreActionItem, MoreActionPopover } from "../common/more-action-popover";
import { EditIcon } from "../agent-dashboard/assets";
import { DeleteConfirmModal } from "../ui/modal";
import { LoadingOverlay } from "../common/loading-overlay";

export interface PanelAgentTemplateProps {
  open: boolean;
  detailLoading?: boolean;
  templates: TemplateItem[];
  showVideoAction: boolean;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (uuid: string) => Promise<void>;
  onEdit: (uuid: string) => void;
}

export const PanelAgentTemplate: React.FC<PanelAgentTemplateProps> = ({
  open,
  detailLoading,
  loading,
  templates,
  showVideoAction,
  onOpenChange,
  onDelete,
  onEdit,
}) => {
  const { t } = useTranslation();
  const [triggerId, setTriggerId] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async (uuid: string) => {
    try {
      setDeleteLoading(true);
      await onDelete(uuid);
    } catch (error) {
      console.log(error)
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/0" />
        <Dialog.Content
          className="
            fixed right-0 top-0 z-50 h-full w-[480px]
            bg-white flex flex-col
            data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full
            transition-transform duration-300
          "
        >
          {/* 头部 */}
          <div className="flex items-center justify-between my-6 px-6">

            <div
              className="flex items-center gap-4 text-[rgba(16,24,40,1)]"
            >
              <ModalCloseButton
                icon={<ChevronLeft className="w-5 h-5" />}
                onClick={() => onOpenChange(false)}
              />
              <span className="text-base font-medium">
                {t("agent_dashboard.user_detail.template.title")}
              </span>
            </div>
            <ModalCloseButton
              onClick={() => onOpenChange(false)}
            />
          </div>

          {/* 全部模板列表 */}
          <div className="flex-1 overflow-auto px-6">
            <div
              className={cn("grid gap-3", !templates?.length && "min-h-full")}
              style={{ gridTemplateColumns: "repeat(4, 1fr)" }}
            >
              {loading && (
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="w-full relative">
                    <div className="mb-2.5 w-full aspect-[3/4] rounded-[8px] bg-gray-200 animate-pulse" />
                    <div className="h-4 bg-gray-200 rounded mx-auto w-2/3 animate-pulse " />
                  </div>
                ))
              )}
              {!loading && templates?.length ? templates.map((tpl) => (
                <AgentTemplateVideoModal
                  key={tpl.id}
                  title={tpl.name}
                  videoUrl={tpl.coverVideo}
                  poster={tpl.coverUrl}
                  trigger={
                    <button className="w-full text-left  group">
                      <div className="mb-2.5 w-full aspect-[3/4] rounded-[10px] overflow-hidden relative">
                        <Image
                          src={tpl.coverUrl}
                          className="size-full object-cover rounded-[10px]"
                          alt={tpl.name}
                        />
                        {/* 更多操作 */}
                        {showVideoAction && (
                          <div
                            className="opacity-0 group-hover:opacity-100 absolute top-1.5 right-1.5 transition-all z-20"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreActionPopover
                              className="w-32"
                              side="bottom"
                              tiggerIcon={
                                <MoreIcon className="size-5 text-white" />
                              }
                              tiggerClassName="hover:bg-white/30 bg-[rgba(0,0,0,0.4)] shadow-sm"
                            >
                              <MoreActionItem
                                icon={<EditIcon className="w-4 h-4" />}
                                label={t("common.edit")}
                                className='text-[rgba(16,24,40,1)] font-medium'
                                onClick={() => {
                                  setTriggerId(tpl.uuid);
                                  onEdit(tpl.uuid)
                                }}
                              />
                              <DeleteConfirmModal
                                trigger={
                                  <MoreActionItem
                                    icon={<TrashIcon className="w-4 h-4" />}
                                    className='text-[rgba(16,24,40,1)] font-medium'
                                    label={t("common.delete")}
                                  />
                                } 
                                loading={deleteLoading}
                                onConfirm={async () => handleDelete(tpl.uuid)}
                              />
                            </MoreActionPopover>
                          </div>
                        )}
                        <LoadingOverlay loading={detailLoading && triggerId === tpl.uuid} />
                      </div>

                      <TruncatedText
                        className="max-w-[90px] text-center text-sm text-[rgba(16,24,40,1)] font-normal"
                        text={tpl.name}
                      />
                    </button>
                  }
                />
              )) : !loading && (
                <div className=' w-full h-full relative flex flex-col justify-center items-center col-span-full gap-2'>
                  <TemplateEmptyIcon className='w-[120px] h-[103px]' />
                  <span className='text-sm text-[rgba(102,112,133,1)]'>{t("agent_dashboard.user_detail.template.template_empty2")}</span>
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
